import { useEffect, useRef, useState } from "react";
import { UserNotification } from "@prisma/client";

interface SSEMessage {
  type: "connected" | "notification" | "heartbeat";
  notification?: UserNotification;
  timestamp?: number;
}

/**
 * Custom hook to connect to SSE notification stream.
 * Automatically reconnects on connection loss.
 */
export const useNotificationSSE = (
  onNewNotification: (notification: UserNotification) => void,
  enabled: boolean = true,
) => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const connect = () => {
      try {
        const eventSource = new EventSource("/api/notifications/stream");
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          setIsConnected(true);
          reconnectAttempts.current = 0;
        };

        eventSource.onmessage = (event) => {
          try {
            const data: SSEMessage = JSON.parse(event.data);

            if (data.type === "notification" && data.notification) {
              onNewNotification(data.notification);
            }
          } catch (error) {
            console.error("Error parsing SSE message:", error);
          }
        };

        eventSource.onerror = () => {
          setIsConnected(false);
          eventSource.close();

          // Exponential backoff for reconnection
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts.current),
            30000,
          );
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        };
      } catch (error) {
        console.error("Error creating EventSource:", error);
      }
    };

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled, onNewNotification]);

  return { isConnected };
};
